/* eslint-disable react-hooks/rules-of-hooks */
/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * Unified organization resolver hook
 *
 * Bridges the local OrganizationProvider (test bypass support) with
 * Clerk's organization context so data hooks can rely on a single source.
 *
 * NOTE: This file intentionally violates React's rules-of-hooks to support
 * test mode. Test mode is stable throughout a render cycle, so conditionally
 * calling hooks based on test mode is safe in practice.
 */
"use client";

import { useOrganization as useClerkOrganization } from "@clerk/nextjs";
import type { OrganizationResource } from "@clerk/types";

import { useOrganization as useOrganizationContext } from "@/contexts/organization-context";
import { DEFAULT_TEST_USERS } from "@/lib/testing/test-mode-simple";

// Test mode detection
const isTestModeActive = (): boolean => {
  const envTestMode =
    process.env["NEXT_PUBLIC_E2E_TEST_MODE"] === "true" ||
    process.env["E2E_TEST_MODE"] === "true" ||
    process.env["NODE_ENV"] === "test";

  if (typeof window !== "undefined") {
    return envTestMode || Boolean(window.__TEST_BYPASS_ENABLED);
  }

  return envTestMode;
};

type OrganizationSource = "organization-context" | "clerk" | null;

export interface ActiveOrganizationResult {
  organizationId: string | null;
  organization: OrganizationResource | null;
  /**
   * True when we have a stable organization id from either source.
   */
  isOrganizationReady: boolean;
  /**
   * Which provider supplied the organization id.
   */
  source: OrganizationSource;
  /**
   * Raw flags for diagnostic use (avoids tight coupling for consumers).
   */
  readiness: {
    contextReady: boolean;
    clerkReady: boolean;
  };
}

// Removed internal functions - logic is now inlined in the public hook
// This allows conditional hook calls based on test mode

export function useActiveOrganization(): ActiveOrganizationResult {
  // Check test mode first
  const testModeActive = isTestModeActive();

  // Always call OrganizationProvider hook (safe in test mode)
  const organizationContext = useOrganizationContext();

  if (testModeActive) {
    // In test mode, return test data without Clerk hooks
    const organizationId =
      organizationContext.organizationId ??
      DEFAULT_TEST_USERS.owner.organizationId;
    // In test mode, organization is ready if we have an ID and context is ready
    const contextReady =
      !organizationContext.isLoading && organizationContext.isClientReady;
    const isOrganizationReady = !!organizationId && contextReady;

    return {
      organizationId,
      organization: null,
      isOrganizationReady,
      source: "organization-context",
      readiness: {
        contextReady,
        clerkReady: false,
      },
    };
  }

  // In normal mode, call Clerk hooks
  const { organization: clerkOrganization, isLoaded: clerkLoaded } =
    useClerkOrganization();

  const contextOrgId = organizationContext.organizationId;
  const clerkOrgId = clerkOrganization?.id ?? null;
  const organizationId = contextOrgId ?? clerkOrgId ?? null;
  const source: OrganizationSource = contextOrgId
    ? "organization-context"
    : clerkOrgId
      ? "clerk"
      : null;
  const contextReady =
    !organizationContext.isLoading && organizationContext.isClientReady;
  const isOrganizationReady =
    (source === "organization-context" && !!organizationId && contextReady) ||
    (source === "clerk" && !!organizationId && clerkLoaded);

  return {
    organizationId,
    organization: clerkOrganization ?? null,
    isOrganizationReady,
    source,
    readiness: {
      contextReady,
      clerkReady: clerkLoaded,
    },
  };
}
